import { ref, unref, type MaybeRef } from 'vue';
import type { ZodSchema } from 'zod';

/**
 * Get extension config from Atruvia publicApi.
 *
 * Note: Does not perform validation!
 *
 * @param id Extension ID
 * @param _window Window object for testing
 * @returns Extension config
 */
export const getConfig = async <T extends object>(
  id: string,
  zodSchema?: ZodSchema,
  _window = self
) => {
  const extensions = await _window.publicApi.getFeatures();
  const config = extensions.find(extension => extension.id === id)?.config as
    | Partial<T>
    | undefined;

  return zodSchema ? zodSchema.parse(config) : config;
};

/**
 * Reactively get extension config from Atruvia pulicApi.
 * Provide a ZodSchema to validate.
 *
 * @param id Extension ID
 */
export const useConfig = <T extends object>(
  id: MaybeRef<string>,
  zodSchema?: MaybeRef<ZodSchema<T>>
) => {
  const loading = ref(true);
  const config = ref<T>();
  const error = ref<Error | null>(null);

  getConfig<T>(unref(id), unref(zodSchema))
    .then(value => (config.value = value as T))
    .catch(err => {
      console.warn(err);
      return (error.value = err);
    })
    .finally(() => (loading.value = false));

  return { loading, error, config };
};
